/**
 * A general-purpose clinical form/template builder — `BACKLOG.md` C10b, scoped down from `emr-po`'s
 * A17 (its own numbering; answered and finalized 2026-08-18/20,
 * `emr-po/docs/research/custom-forms-and-templates.md`). A17's full architecture (per-practice
 * forking of shipped standard templates with opt-in pull-forward, sub-forms with a site-level
 * pin/float resolution policy, a multi-signer cosign ledger shared with `clinical_notes`, value-set
 * management, a drag-and-drop conditional-logic and scoring-band authoring surface, a FHIR
 * `Observation` projection) is **not** built here — none of it exists anywhere in this codebase yet,
 * and building it now would be inventing a shape ahead of a second real consumer, the same
 * "recurrence hasn't happened yet" reasoning `credentials.ts`'s own header gives for not building
 * A17's fuller `signature_requirements` ledger prematurely.
 *
 * **What v1 actually needs**, per A17's own architecture note and the market research it cites: an
 * admin-authored structured template (a flat ordered list of fields — text/choice/numeric/date/
 * checkbox, the "at minimum" set the research names as the market floor across DrChrono/Tebra/
 * CureMD/Practice Fusion/AdvancedMD/Elation/RXNT/NextGen/Greenway), and a clinician-filled *instance*
 * of it against a specific patient (optionally an encounter) that lands as genuinely structured,
 * per-field data — the line the research draws (§2) between a real form builder and "a PDF with
 * fillable fields."
 *
 * **Versioning, without a third table.** `db/schema/forms.ts`'s `formTemplates` table is
 * append-only: editing a template never updates an existing row's `name`/`fields` — it inserts a new
 * row sharing the same `templateFamilyId` with `version` one higher. A `form_submissions` row always
 * stores the specific `form_templates.id` (a specific version row, not the family) it was filled
 * against, so a later edit to the family can never retroactively change what an existing submission
 * means — the same "append rather than mutate" discipline this codebase's own migrations follow,
 * applied to template content instead of schema. `db/schema/columns.ts`'s `archivedAt` (not
 * `deletedAt`) is what a template row gets when retired from the "start a new submission" picker,
 * matching that helper's own distinction exactly: a retired template version must keep resolving for
 * every submission that already points at it.
 *
 * **Deliberately deferred, named explicitly rather than silently absent:**
 *   - Conditional/branching logic within a form (field X only shown or required if field Y = value).
 *   - A template marketplace or any cross-org template sharing.
 *   - AI-assisted template authoring.
 *   - PDF export of a filled submission — `C8`'s `pdfService.ts` exists and is a natural future
 *     consumer, but wiring that integration is out of scope here.
 *   - Sub-forms/composability, standard-template forking-at-provision, cosign, value-set tables, and
 *     the FHIR projection — A17's own fuller design, noted above.
 *   - Editing a submitted response. A submission is create-then-immutable (soft-deletable only, for
 *     mistake correction) — no PATCH exists. This is a smaller, honest subset of A17's own "immutable
 *     completed response" principle, without that design's addendum mechanism.
 */

import { z } from 'zod';

import { confidentialityReasonsInputSchema } from './clinicalRequests.ts';

// --- Field definitions -------------------------------------------------------------------------------

/** The market-floor field-type set (this module's own header). Closed, extended deliberately. */
export const FORM_FIELD_TYPES = ['text', 'choice', 'numeric', 'date', 'checkbox'] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const MAX_FORM_FIELD_KEY_LENGTH = 64;
export const MAX_FORM_FIELD_LABEL_LENGTH = 200;
export const MAX_FORM_FIELD_HELP_TEXT_LENGTH = 500;
export const MAX_FORM_FIELD_CHOICES = 50;
export const MAX_FORM_FIELD_CHOICE_LENGTH = 200;
export const MAX_FORM_TEMPLATE_FIELDS = 100;
export const MAX_FORM_TEMPLATE_NAME_LENGTH = 200;
export const MAX_FORM_TEMPLATE_DESCRIPTION_LENGTH = 1000;
export const MAX_FORM_TEXT_VALUE_LENGTH = 5000;

/**
 * A field's own stable identifier within its template — what `form_submissions.values` keys are, and
 * what survives a field being reordered or relabeled across a template's own edit history within one
 * version. Deliberately restrictive (lowercase snake_case) rather than accepting arbitrary text: this
 * key is a storage key, not a display string, and `label` already carries the human-readable name.
 */
const fieldKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_FORM_FIELD_KEY_LENGTH)
  .regex(/^[a-z][a-z0-9_]*$/, 'must be lowercase snake_case starting with a letter');

const baseFieldShape = {
  key: fieldKeySchema,
  label: z.string().trim().min(1).max(MAX_FORM_FIELD_LABEL_LENGTH),
  required: z.boolean().default(false),
  helpText: z.string().trim().min(1).max(MAX_FORM_FIELD_HELP_TEXT_LENGTH).nullable().default(null),
};

/**
 * One field definition. A discriminated union on `type`, matching `signatureRequests.ts`'s own
 * `updateSignatureSettingsRequestSchema` precedent: each variant is `strictObject`, so a `'checkbox'`
 * field carrying a stray `choices` array is a rejection, not a silently ignored extra.
 */
export const formFieldDefinitionSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('text'),
    ...baseFieldShape,
    maxLength: z.number().int().positive().max(MAX_FORM_TEXT_VALUE_LENGTH).nullable().default(null),
  }),
  z.strictObject({
    type: z.literal('choice'),
    ...baseFieldShape,
    choices: z
      .array(z.string().trim().min(1).max(MAX_FORM_FIELD_CHOICE_LENGTH))
      .min(1)
      .max(MAX_FORM_FIELD_CHOICES),
  }),
  z.strictObject({
    type: z.literal('numeric'),
    ...baseFieldShape,
    min: z.number().nullable().default(null),
    max: z.number().nullable().default(null),
  }),
  z.strictObject({ type: z.literal('date'), ...baseFieldShape }),
  z.strictObject({ type: z.literal('checkbox'), ...baseFieldShape }),
]);
export type FormFieldDefinition = z.infer<typeof formFieldDefinitionSchema>;

/** A template's whole field list: at least one field, capped, and every `key` unique — a duplicate
 * key would make `form_submissions.values` ambiguous about which field definition it belongs to. */
export const formFieldsSchema = z
  .array(formFieldDefinitionSchema)
  .min(1)
  .max(MAX_FORM_TEMPLATE_FIELDS)
  .refine((fields) => new Set(fields.map((f) => f.key)).size === fields.length, {
    message: 'field keys must be unique within a template',
  });

/**
 * Narrows a `form_templates.fields` `jsonb` column's untyped storage back to `FormFieldDefinition[]`
 * — every token-scoped module reading a pinned template's fields to render or validate against
 * (`patientSurvey/service.ts`, `patientIntake/service.ts`) needs this identical cast, so it lives
 * here once rather than as a verbatim per-module copy (`~/repos/claude-kit/rules/working-style.md`'s
 * "favor the general, reusable solution once a pattern actually recurs" — a second occurrence had
 * already happened before this extraction, not a first-occurrence abstraction being built ahead of
 * need). `.parse`, not `.safeParse`: `fields` here always comes from a `form_templates` row this
 * codebase itself wrote through `createFormTemplateFamily`/`publishFormTemplateVersion`, which
 * already validate against this exact schema before insert — an actual mismatch here would mean
 * storage corruption, not untrusted input, the same trust boundary `formTemplatesService.ts`'s own
 * `TEMPLATE_COLUMNS` reads rely on.
 */
export function narrowFormFields(fields: unknown): readonly FormFieldDefinition[] {
  return formFieldsSchema.parse(fields);
}

// --- Submission values --------------------------------------------------------------------------------

/** What one filled field can hold on the wire. `null` means "left blank" — distinct from the key
 * being absent entirely, both of which `validateFormSubmissionValues` below treat as "not present"
 * for a required-field check, but only an absent key is flagged `unknown_field` when it is not. */
export type FormFieldValue = string | number | boolean | null;

export const formSubmissionValuesSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);
export type FormSubmissionValues = z.infer<typeof formSubmissionValuesSchema>;

export const FORM_VALUE_ERROR_REASONS = [
  /** A required field has no value (absent key, or an explicit `null`). */
  'required',
  /** `values` carries a key with no matching field in the pinned template version. */
  'unknown_field',
  /** The value's JS type does not match what this field type stores. */
  'wrong_type',
  /** A `choice` field's value is not one of its own `choices`. */
  'not_a_choice',
  /** A `numeric` field's value is outside its own `min`/`max`, or a `text` field's value is longer
   * than its own `maxLength`. */
  'out_of_range',
  /** A `date` field's value is not a well-formed `YYYY-MM-DD` calendar date. */
  'invalid_date',
] as const;
export type FormValueErrorReason = (typeof FORM_VALUE_ERROR_REASONS)[number];

export interface FormValueValidationError {
  readonly fieldKey: string;
  readonly reason: FormValueErrorReason;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The actual "genuinely structured data, not a blob" enforcement this module's own header names as
 * the point of the whole feature — pure, so both the server (the authoritative check,
 * `formSubmissionsService.ts`) and the web app (pre-submit validation in the fill UI) run the exact
 * same rule rather than two hand-written approximations of it.
 *
 * Every field in `fields` is checked (required-but-absent), and every key in `values` is checked
 * (must name a real field) — the two directions a validated set of values can go wrong. Returns every
 * error found rather than stopping at the first, so a fill UI can show every problem at once instead
 * of one submit attempt per mistake.
 */
export function validateFormSubmissionValues(
  fields: readonly FormFieldDefinition[],
  values: FormSubmissionValues,
): readonly FormValueValidationError[] {
  const errors: FormValueValidationError[] = [];
  const fieldByKey = new Map(fields.map((field) => [field.key, field]));

  for (const key of Object.keys(values)) {
    if (!fieldByKey.has(key)) errors.push({ fieldKey: key, reason: 'unknown_field' });
  }

  for (const field of fields) {
    const raw = values[field.key];
    const present = raw !== undefined && raw !== null;
    if (!present) {
      if (field.required) errors.push({ fieldKey: field.key, reason: 'required' });
      continue;
    }

    switch (field.type) {
      case 'text':
        if (typeof raw !== 'string') {
          errors.push({ fieldKey: field.key, reason: 'wrong_type' });
        } else if (field.maxLength !== null && raw.length > field.maxLength) {
          errors.push({ fieldKey: field.key, reason: 'out_of_range' });
        }
        break;
      case 'choice':
        if (typeof raw !== 'string') {
          errors.push({ fieldKey: field.key, reason: 'wrong_type' });
        } else if (!(field.choices as readonly string[]).includes(raw)) {
          errors.push({ fieldKey: field.key, reason: 'not_a_choice' });
        }
        break;
      case 'numeric':
        if (typeof raw !== 'number' || !Number.isFinite(raw)) {
          errors.push({ fieldKey: field.key, reason: 'wrong_type' });
        } else if (
          (field.min !== null && raw < field.min) ||
          (field.max !== null && raw > field.max)
        ) {
          errors.push({ fieldKey: field.key, reason: 'out_of_range' });
        }
        break;
      case 'date':
        if (typeof raw !== 'string' || !ISO_DATE_RE.test(raw) || Number.isNaN(Date.parse(raw))) {
          errors.push({ fieldKey: field.key, reason: 'invalid_date' });
        }
        break;
      case 'checkbox':
        if (typeof raw !== 'boolean') errors.push({ fieldKey: field.key, reason: 'wrong_type' });
        break;
    }
  }

  return errors;
}

// --- Request/response schemas -------------------------------------------------------------------------

export const FORM_TEMPLATE_ERROR_CODES = [
  'invalid_request',
  'not_found',
  /** The referenced template version is archived — a new submission may not start against it, even
   * though a submission already pointing at it must still read fine (this module's own header). */
  'template_archived',
] as const;
export type FormTemplateErrorCode = (typeof FORM_TEMPLATE_ERROR_CODES)[number];

export const FORM_SUBMISSION_ERROR_CODES = [
  'invalid_request',
  'not_found',
  'template_archived',
  /** `values` failed `validateFormSubmissionValues` against the pinned template version's fields. */
  'invalid_values',
] as const;
export type FormSubmissionErrorCode = (typeof FORM_SUBMISSION_ERROR_CODES)[number];

/** Create a brand-new template family (version 1) or publish the next version of an existing one —
 * same body shape either way, since both are "here is the template's full content as of now." */
export const createFormTemplateRequestSchema = z.strictObject({
  name: z.string().trim().min(1).max(MAX_FORM_TEMPLATE_NAME_LENGTH),
  description: z.string().trim().max(MAX_FORM_TEMPLATE_DESCRIPTION_LENGTH).nullable().default(null),
  fields: formFieldsSchema,
});
export type CreateFormTemplateRequest = z.infer<typeof createFormTemplateRequestSchema>;

export const formTemplateResponseSchema = z.object({
  id: z.uuid(),
  templateFamilyId: z.uuid(),
  version: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  fields: formFieldsSchema,
  createdByUserId: z.uuid(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type FormTemplateResponse = z.infer<typeof formTemplateResponseSchema>;

export const formTemplateEnvelopeSchema = z.object({ template: formTemplateResponseSchema });
export const formTemplateListEnvelopeSchema = z.object({
  templates: z.array(formTemplateResponseSchema),
});

export const createFormSubmissionRequestSchema = z.strictObject({
  templateId: z.uuid(),
  encounterId: z.uuid().nullable().default(null),
  values: formSubmissionValuesSchema,
  confidentialityReasons: confidentialityReasonsInputSchema,
});
export type CreateFormSubmissionRequest = z.infer<typeof createFormSubmissionRequestSchema>;

export const formSubmissionResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid().nullable(),
  templateId: z.uuid(),
  values: formSubmissionValuesSchema,
  submittedByUserId: z.uuid(),
  submittedAt: z.string(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type FormSubmissionResponse = z.infer<typeof formSubmissionResponseSchema>;

export const formSubmissionEnvelopeSchema = z.object({ submission: formSubmissionResponseSchema });
export const formSubmissionListEnvelopeSchema = z.object({
  submissions: z.array(formSubmissionResponseSchema),
});

/**
 * A submission's own list/detail response carries only `templateId`, not the template's fields
 * inline — deliberately, to keep the list route's payload small. A viewer that needs to render a
 * submission's answers (a field key needs its label/type to display meaningfully) fetches the pinned
 * version directly via `GET /api/form-templates/:templateId` (`formTemplatesRoutes.ts`), which
 * resolves fully regardless of whether that version is still current or has since been archived —
 * this module's own header explains why that resolves correctly no matter how much later the family
 * has been edited. `web/src/routes/patients/PatientFormsPage.tsx` does exactly this, one query per
 * distinct `templateId` actually present in the list, not a batched combined endpoint.
 */
